/**
 * Audit Log View - GovBR Design System
 * @module components/AuditLog/AuditLog
 */

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

import React, { createRef, useEffect, useState } from 'react';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import {
  getAuditLog,
  getAuditLogCsv,
  getPortalTypes,
  resetAuditLog,
} from '../../actions';

import backSVG from '@plone/volto/icons/back.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import leftKey from '@plone/volto/icons/left-key.svg';
import rightKey from '@plone/volto/icons/right-key.svg';

const messages = defineMessages({
  auditLog: {
    id: 'Audit Log',
    defaultMessage: 'Relatório de Auditoria',
  },
  back: {
    id: 'Back',
    defaultMessage: 'Voltar',
  },
  loading: {
    id: 'Loading...',
    defaultMessage: 'Carregando...',
  },
  error: {
    id: 'Error loading audit log',
    defaultMessage: 'Erro ao carregar o relatório de auditoria',
  },
  noResults: {
    id: 'No results found',
    defaultMessage: 'Nenhum resultado encontrado',
  },
  action: {
    id: 'Action',
    defaultMessage: 'Ação',
  },
  title: {
    id: 'Title',
    defaultMessage: 'Título',
  },
  path: {
    id: 'Path',
    defaultMessage: 'Caminho',
  },
  contentType: {
    id: 'Content Type',
    defaultMessage: 'Tipo',
  },
  creationUser: {
    id: 'Creation User',
    defaultMessage: 'Criado por',
  },
  modificationUser: {
    id: 'Modification User',
    defaultMessage: 'Modificado por',
  },
  creationDate: {
    id: 'Creation Date',
    defaultMessage: 'Data de criação',
  },
  modificationDate: {
    id: 'Modification Date',
    defaultMessage: 'Data de modificação',
  },
  searchPath: {
    id: 'Search by path',
    defaultMessage: 'Buscar por caminho',
  },
  searchPathPlaceholder: {
    id: 'Search path placeholder',
    defaultMessage:
      'Use * para buscar parte do caminho (ex: */pasta/* ou *nome*)',
  },
  searchPathLabel: {
    id: 'Search path label',
    defaultMessage: 'Caminho',
  },
  searchCpfLabel: {
    id: 'Search CPF label',
    defaultMessage: 'CPF',
  },
  searchCpfPlaceholder: {
    id: 'Search CPF placeholder',
    defaultMessage: 'Digite apenas números',
  },
  filterByCreationDate: {
    id: 'Filter by creation date',
    defaultMessage: 'Data de criação',
  },
  filterByModificationDate: {
    id: 'Filter by modification date',
    defaultMessage: 'Data de modificação',
  },
  startDate: {
    id: 'Start Date',
    defaultMessage: 'Data inicial',
  },
  endDate: {
    id: 'End Date',
    defaultMessage: 'Data final',
  },
  filterByType: {
    id: 'Filter by content type',
    defaultMessage: 'Tipo de Conteúdo',
  },
  filterByAction: {
    id: 'Filter by action',
    defaultMessage: 'Ação',
  },
  filter: {
    id: 'Filter',
    defaultMessage: 'Filtrar',
  },
  clearFilters: {
    id: 'Clear filters',
    defaultMessage: 'Limpar',
  },
  itemsShowing: {
    id: 'Items showing',
    defaultMessage: 'itens',
  },
  previousPage: {
    id: 'Previous page',
    defaultMessage: 'Página anterior',
  },
  nextPage: {
    id: 'Next page',
    defaultMessage: 'Próxima página',
  },
  sortBy: {
    id: 'Sort by',
    defaultMessage: 'Ordenar por',
  },
  sortByCreationDate: {
    id: 'Sort by creation date',
    defaultMessage: 'Data de criação',
  },
  sortByModificationDate: {
    id: 'Sort by modification date',
    defaultMessage: 'Data de modificação',
  },
  sortOrder: {
    id: 'Sort order',
    defaultMessage: 'Ordem',
  },
  ascending: {
    id: 'Ascending',
    defaultMessage: 'Ascendente',
  },
  descending: {
    id: 'Descending',
    defaultMessage: 'Descendente',
  },
  create: {
    id: 'Create',
    defaultMessage: 'Criar',
  },
  modify: {
    id: 'Modify',
    defaultMessage: 'Modificar',
  },
  delete: {
    id: 'Delete',
    defaultMessage: 'Deletar',
  },
  changeState: {
    id: 'Change state',
    defaultMessage: 'Alterar estado',
  },
  move: {
    id: 'Move',
    defaultMessage: 'Mover',
  },
  display: {
    id: 'Display',
    defaultMessage: 'Exibir',
  },
  of: {
    id: 'of',
    defaultMessage: 'de',
  },
  page: {
    id: 'Page',
    defaultMessage: 'Página',
  },
  goBack: {
    id: 'Go back',
    defaultMessage: 'Voltar página',
  },
  goForward: {
    id: 'Go forward',
    defaultMessage: 'Página seguinte',
  },
  showList: {
    id: 'Show list',
    defaultMessage: 'Exibir lista',
  },
  exportCsv: {
    id: 'Export to CSV',
    defaultMessage: 'Exportar para CSV',
  },
  exporting: {
    id: 'Exporting...',
    defaultMessage: 'Exportando...',
  },
  exportingRecords: {
    id: 'Exporting records',
    defaultMessage: 'Exportando {total} registros...',
  },
});

/**
 * Audit Log component
 * @class AuditLog
 * @extends Component
 */
const PrevIcon = () => (
  <div
    className="prev-icon"
    role="button"
    style={{
      color: '#000',
      left: '22px',
      padding: '5px',
      position: 'absolute',
      top: '15px',
    }}
    tabIndex="0"
  >
    <Icon name={leftKey} size="30px" />
  </div>
);

const NextIcon = () => (
  <div
    className="next-icon"
    role="button"
    style={{
      color: '#000',
      right: '22px',
      padding: '5px',
      position: 'absolute',
      top: '15px',
    }}
    tabIndex="0"
  >
    <Icon name={rightKey} size="30px" />
  </div>
);

const CloseIcon = () => <Icon name={clearSVG} size="24px" className="close" />;

const AuditLogComponent = ({ reactDates, moment: momentlib }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const moment = momentlib.default;
  const { DateRangePicker } = reactDates;
  const lang = useSelector((state) => state.intl.locale);

  const { items, loading, loaded, error, total } = useSelector(
    (state) => state.auditLog,
  );

  const portalTypesData = useSelector((state) => state.portalTypes);

  const [filters, setFilters] = useState({
    cpf: '',
    path: '',
    portal_type: [],
    action: [],
    creation_date: [null, null],
    modification_date: [null, null],
  });

  const [sortOn, setSortOn] = useState('creation_date');
  const [sortOrder, setSortOrder] = useState('desc');

  const [creationDateFocused, setCreationDateFocused] = useState(null);
  const [modificationDateFocused, setModificationDateFocused] = useState(null);

  const [pageSize, setPageSize] = useState(5);
  const [activePage, setActivePage] = useState(1);
  const [isClient, setIsClient] = useState(false);
  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
  const [showGoToPageDropdown, setShowGoToPageDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const listingRef = createRef();

  useEffect(() => {
    dispatch(getPortalTypes());
    setIsClient(true);

    // Limpar estado quando o componente é desmontado
    return () => {
      dispatch(resetAuditLog());
    };
  }, [dispatch]);

  const fetchData = (page = 1, size = pageSize) => {
    const start = (page - 1) * size;
    const portalTypeFilter = Array.isArray(filters.portal_type)
      ? filters.portal_type.join(',')
      : filters.portal_type;
    const actionFilter = Array.isArray(filters.action)
      ? filters.action.join(',')
      : filters.action;

    // Format dates to DD/MM/YYYY if present
    const creationDateStart =
      filters.creation_date[0] && filters.creation_date[0].format
        ? filters.creation_date[0].format('DD/MM/YYYY')
        : '';
    const creationDateEnd =
      filters.creation_date[1] && filters.creation_date[1].format
        ? filters.creation_date[1].format('DD/MM/YYYY')
        : '';
    const modificationDateStart =
      filters.modification_date[0] && filters.modification_date[0].format
        ? filters.modification_date[0].format('DD/MM/YYYY')
        : '';
    const modificationDateEnd =
      filters.modification_date[1] && filters.modification_date[1].format
        ? filters.modification_date[1].format('DD/MM/YYYY')
        : '';

    dispatch(
      getAuditLog(
        filters.path,
        start,
        size,
        portalTypeFilter,
        filters.cpf,
        actionFilter,
        creationDateStart,
        creationDateEnd,
        modificationDateStart,
        modificationDateEnd,
        sortOn,
        sortOrder,
      ),
    );
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleFilter = () => {
    setActivePage(1);
    fetchData(1, pageSize);
  };

  const handleClearFilters = () => {
    setFilters({
      cpf: '',
      path: '',
      portal_type: [],
      action: [],
      creation_date: [null, null],
      modification_date: [null, null],
    });
    setSortOn('creation_date');
    setSortOrder('desc');
    setActivePage(1);
    dispatch(resetAuditLog());
  };

  const handlePortalTypeToggle = (portalTypeId) => {
    const currentTypes = filters.portal_type;
    const newTypes = currentTypes.includes(portalTypeId)
      ? currentTypes.filter((id) => id !== portalTypeId)
      : [...currentTypes, portalTypeId];

    setFilters({
      ...filters,
      portal_type: newTypes,
    });
  };

  const handleActionToggle = (actionId) => {
    const currentActions = filters.action;
    const newActions = currentActions.includes(actionId)
      ? currentActions.filter((id) => id !== actionId)
      : [...currentActions, actionId];

    setFilters({
      ...filters,
      action: newActions,
    });
  };

  const handlePageChange = (e, { activePage: page }) => {
    setActivePage(page);
    fetchData(page, pageSize);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setActivePage(1);
    fetchData(1, size);
  };

  const totalPages = Math.ceil(total / pageSize);
  const prevBatch = activePage > 1;
  const nextBatch = activePage < totalPages;

  const handleExportCsv = async () => {
    if (total === 0) return;

    setIsExporting(true);

    try {
      // Prepare parameters for API call (same filters, but fetch ALL records)
      const portalTypeFilter = Array.isArray(filters.portal_type)
        ? filters.portal_type.join(',')
        : filters.portal_type;
      const actionFilter = Array.isArray(filters.action)
        ? filters.action.join(',')
        : filters.action;

      // Format dates to DD/MM/YYYY if present
      const creationDateStart =
        filters.creation_date[0] && filters.creation_date[0].format
          ? filters.creation_date[0].format('DD/MM/YYYY')
          : '';
      const creationDateEnd =
        filters.creation_date[1] && filters.creation_date[1].format
          ? filters.creation_date[1].format('DD/MM/YYYY')
          : '';
      const modificationDateStart =
        filters.modification_date[0] && filters.modification_date[0].format
          ? filters.modification_date[0].format('DD/MM/YYYY')
          : '';
      const modificationDateEnd =
        filters.modification_date[1] && filters.modification_date[1].format
          ? filters.modification_date[1].format('DD/MM/YYYY')
          : '';

      // Use the action to get CSV data
      const result = await dispatch(
        getAuditLogCsv(
          filters.path,
          0,
          total,
          portalTypeFilter,
          filters.cpf,
          actionFilter,
          creationDateStart,
          creationDateEnd,
          modificationDateStart,
          modificationDateEnd,
          sortOn,
          sortOrder,
        ),
      );

      const allItems = result?.items || [];

      if (!allItems || allItems.length === 0) {
        setIsExporting(false);
        return;
      }

      // Define headers
      const headers = [
        intl.formatMessage(messages.action),
        intl.formatMessage(messages.title),
        intl.formatMessage(messages.path),
        intl.formatMessage(messages.contentType),
        intl.formatMessage(messages.creationUser),
        intl.formatMessage(messages.creationDate),
        intl.formatMessage(messages.modificationUser),
        intl.formatMessage(messages.modificationDate),
      ];

      // Convert action to localized text
      const getActionText = (action) => {
        const actionMap = {
          create: intl.formatMessage(messages.create),
          modify: intl.formatMessage(messages.modify),
          delete: intl.formatMessage(messages.delete),
          'change-state': intl.formatMessage(messages.changeState),
          move: intl.formatMessage(messages.move),
        };
        return actionMap[action] || action;
      };

      // Prepare data rows from ALL items
      const rows = allItems.map((item) => [
        getActionText(item.action),
        item.title,
        item.path,
        portalTypeMap[item.portal_type] || item.portal_type,
        item.creation_user || '',
        item.creation_date || '',
        item.modification_user || '',
        item.modification_date || '',
      ]);

      // Escape CSV fields (handle quotes and commas)
      const escapeCsvField = (field) => {
        const stringField = String(field);
        if (
          stringField.includes(',') ||
          stringField.includes('"') ||
          stringField.includes('\n')
        ) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      // Build filter information section
      const filterInfo = [];
      filterInfo.push(['FILTROS APLICADOS', '']);
      filterInfo.push(['', '']);

      if (filters.cpf) {
        filterInfo.push([
          intl.formatMessage(messages.searchCpfLabel),
          filters.cpf,
        ]);
      }

      if (filters.path) {
        filterInfo.push([
          intl.formatMessage(messages.searchPathLabel),
          filters.path,
        ]);
      }

      if (filters.portal_type.length > 0) {
        const typeNames = filters.portal_type
          .map((typeId) => portalTypeMap[typeId] || typeId)
          .join(', ');
        filterInfo.push([intl.formatMessage(messages.filterByType), typeNames]);
      }

      if (filters.action.length > 0) {
        const actionNames = filters.action
          .map((actionId) => {
            const actionMap = {
              create: intl.formatMessage(messages.create),
              modify: intl.formatMessage(messages.modify),
              delete: intl.formatMessage(messages.delete),
              'change-state': intl.formatMessage(messages.changeState),
              move: intl.formatMessage(messages.move),
            };
            return actionMap[actionId] || actionId;
          })
          .join(', ');
        filterInfo.push([
          intl.formatMessage(messages.filterByAction),
          actionNames,
        ]);
      }

      if (creationDateStart || creationDateEnd) {
        const dateRange = `${creationDateStart || '...'} - ${creationDateEnd || '...'}`;
        filterInfo.push([
          intl.formatMessage(messages.filterByCreationDate),
          dateRange,
        ]);
      }

      if (modificationDateStart || modificationDateEnd) {
        const dateRange = `${modificationDateStart || '...'} - ${modificationDateEnd || '...'}`;
        filterInfo.push([
          intl.formatMessage(messages.filterByModificationDate),
          dateRange,
        ]);
      }

      filterInfo.push([
        intl.formatMessage(messages.sortBy),
        sortOn === 'creation_date'
          ? intl.formatMessage(messages.sortByCreationDate)
          : intl.formatMessage(messages.sortByModificationDate),
      ]);

      filterInfo.push([
        intl.formatMessage(messages.sortOrder),
        sortOrder === 'asc'
          ? intl.formatMessage(messages.ascending)
          : intl.formatMessage(messages.descending),
      ]);

      filterInfo.push(['Total de registros', total.toString()]);
      filterInfo.push(['', '']);

      // Build CSV content with filters and data
      const csvContent = [
        // Filter information
        ...filterInfo.map((row) => row.map(escapeCsvField).join(',')),
        // Empty line separator
        '',
        // Data headers and rows
        headers.map(escapeCsvField).join(','),
        ...rows.map((row) => row.map(escapeCsvField).join(',')),
      ].join('\n');

      // Add BOM for UTF-8 encoding (helps Excel open correctly)
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], {
        type: 'text/csv;charset=utf-8;',
      });

      // Create download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Generate filename with timestamp
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      link.setAttribute('download', `relatorio-auditoria_${timestamp}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      // Error exporting CSV
    } finally {
      setIsExporting(false);
    }
  };

  // Criar mapeamento de portal_type id para title
  const portalTypeMap = {};
  if (portalTypesData.loaded && portalTypesData.items) {
    portalTypesData.items.forEach((type) => {
      portalTypeMap[type.id] = type.title;
    });
  }

  return (
    <div id="page-audit-log" className="block search">
      <Helmet title={intl.formatMessage(messages.auditLog)} />

      <div ref={listingRef} className="ui container">
        <h1 className="documentFirstHeading">
          {intl.formatMessage(messages.auditLog)}
        </h1>

        {/* Filtros */}
        <div className="mb-4">
          <div className="row g-2 mb-3">
            <div className="col-md-5 mb-3">
              <div className="br-input">
                <label htmlFor="input-cpf">
                  {intl.formatMessage(messages.searchCpfLabel)}
                </label>
                <input
                  id="input-cpf"
                  type="text"
                  placeholder={intl.formatMessage(
                    messages.searchCpfPlaceholder,
                  )}
                  value={filters.cpf}
                  maxLength="11"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    handleFilterChange('cpf', value);
                  }}
                />
              </div>
            </div>
            <div className="col-md-7 mb-3">
              <div className="br-input">
                <label htmlFor="input-path">
                  {intl.formatMessage(messages.searchPathLabel)}
                </label>
                <input
                  id="input-path"
                  type="text"
                  placeholder={intl.formatMessage(
                    messages.searchPathPlaceholder,
                  )}
                  value={filters.path}
                  onChange={(e) => handleFilterChange('path', e.target.value)}
                />
              </div>
            </div>
            <div className="col-12 mb-3">
              <label className="d-block mb-2 fw-bold">
                {intl.formatMessage(messages.filterByType)}
              </label>
              <div>
                {portalTypesData.loaded &&
                  portalTypesData.items.map((type) => (
                    <div key={type.id} className="d-inline-block mr-5">
                      <div className="br-checkbox">
                        <input
                          id={`type-${type.id}`}
                          name={`type-${type.id}`}
                          type="checkbox"
                          checked={filters.portal_type.includes(type.id)}
                          onChange={() => handlePortalTypeToggle(type.id)}
                        />
                        <label htmlFor={`type-${type.id}`}>{type.title}</label>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div className="col-12 mb-3">
              <label className="d-block mb-2 fw-bold">
                {intl.formatMessage(messages.filterByAction)}
              </label>
              <div>
                <div className="d-inline-block mr-5">
                  <div className="br-checkbox">
                    <input
                      id="action-create"
                      name="action-create"
                      type="checkbox"
                      checked={filters.action.includes('create')}
                      onChange={() => handleActionToggle('create')}
                    />
                    <label htmlFor="action-create">
                      {intl.formatMessage(messages.create)}
                    </label>
                  </div>
                </div>
                <div className="d-inline-block mr-5">
                  <div className="br-checkbox">
                    <input
                      id="action-modify"
                      name="action-modify"
                      type="checkbox"
                      checked={filters.action.includes('modify')}
                      onChange={() => handleActionToggle('modify')}
                    />
                    <label htmlFor="action-modify">
                      {intl.formatMessage(messages.modify)}
                    </label>
                  </div>
                </div>
                <div className="d-inline-block mr-5">
                  <div className="br-checkbox">
                    <input
                      id="action-delete"
                      name="action-delete"
                      type="checkbox"
                      checked={filters.action.includes('delete')}
                      onChange={() => handleActionToggle('delete')}
                    />
                    <label htmlFor="action-delete">
                      {intl.formatMessage(messages.delete)}
                    </label>
                  </div>
                </div>
                <div className="d-inline-block mr-5">
                  <div className="br-checkbox">
                    <input
                      id="action-change-state"
                      name="action-change-state"
                      type="checkbox"
                      checked={filters.action.includes('change-state')}
                      onChange={() => handleActionToggle('change-state')}
                    />
                    <label htmlFor="action-change-state">
                      {intl.formatMessage(messages.changeState)}
                    </label>
                  </div>
                </div>
                <div className="d-inline-block mr-5">
                  <div className="br-checkbox">
                    <input
                      id="action-move"
                      name="action-move"
                      type="checkbox"
                      checked={filters.action.includes('move')}
                      onChange={() => handleActionToggle('move')}
                    />
                    <label htmlFor="action-move">
                      {intl.formatMessage(messages.move)}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-5 mb-3">
              <div className="daterange-facet">
                <label className="d-block mb-2 fw-bold">
                  {intl.formatMessage(messages.filterByCreationDate)}
                </label>
                <div className="ui form date-time-widget-wrapper">
                  <div className="ui input date-input">
                    <DateRangePicker
                      startDate={
                        filters.creation_date[0]
                          ? moment(filters.creation_date[0])
                          : null
                      }
                      startDateId="creation-start-date"
                      startDatePlaceholderText={intl.formatMessage(
                        messages.startDate,
                      )}
                      endDate={
                        filters.creation_date[1]
                          ? moment(filters.creation_date[1])
                          : null
                      }
                      endDateId="creation-end-date"
                      endDatePlaceholderText={intl.formatMessage(
                        messages.endDate,
                      )}
                      numberOfMonths={1}
                      noBorder
                      showClearDates
                      customCloseIcon={<CloseIcon />}
                      displayFormat={moment
                        .localeData(toBackendLang(lang))
                        .longDateFormat('L')}
                      focusedInput={creationDateFocused}
                      onFocusChange={(focusedInput) =>
                        setCreationDateFocused(focusedInput)
                      }
                      onDatesChange={({ startDate, endDate }) => {
                        setFilters({
                          ...filters,
                          creation_date: [
                            startDate ? startDate.startOf('day') : null,
                            endDate ? endDate.endOf('day') : null,
                          ],
                        });
                      }}
                      isOutsideRange={() => false}
                      minimumNights={0}
                      navPrev={<PrevIcon />}
                      navNext={<NextIcon />}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-7 mb-3">
              <div className="daterange-facet">
                <label className="d-block mb-2 fw-bold">
                  {intl.formatMessage(messages.filterByModificationDate)}
                </label>
                <div className="ui form date-time-widget-wrapper">
                  <div className="ui input date-input">
                    <DateRangePicker
                      startDate={
                        filters.modification_date[0]
                          ? moment(filters.modification_date[0])
                          : null
                      }
                      startDateId="modification-start-date"
                      startDatePlaceholderText={intl.formatMessage(
                        messages.startDate,
                      )}
                      endDate={
                        filters.modification_date[1]
                          ? moment(filters.modification_date[1])
                          : null
                      }
                      endDateId="modification-end-date"
                      endDatePlaceholderText={intl.formatMessage(
                        messages.endDate,
                      )}
                      numberOfMonths={1}
                      noBorder
                      showClearDates
                      customCloseIcon={<CloseIcon />}
                      displayFormat={moment
                        .localeData(toBackendLang(lang))
                        .longDateFormat('L')}
                      focusedInput={modificationDateFocused}
                      onFocusChange={(focusedInput) =>
                        setModificationDateFocused(focusedInput)
                      }
                      onDatesChange={({ startDate, endDate }) => {
                        setFilters({
                          ...filters,
                          modification_date: [
                            startDate ? startDate.startOf('day') : null,
                            endDate ? endDate.endOf('day') : null,
                          ],
                        });
                      }}
                      isOutsideRange={() => false}
                      minimumNights={0}
                      navPrev={<PrevIcon />}
                      navNext={<NextIcon />}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-5 mb-3">
              <label className="d-block mb-2 fw-bold">
                {intl.formatMessage(messages.sortBy)}
              </label>
              <div className="d-inline-block mr-5">
                <div className="br-radio">
                  <input
                    id="sort-creation-date"
                    name="sort-on"
                    type="radio"
                    checked={sortOn === 'creation_date'}
                    onChange={() => setSortOn('creation_date')}
                  />
                  <label htmlFor="sort-creation-date">
                    {intl.formatMessage(messages.sortByCreationDate)}
                  </label>
                </div>
              </div>
              <div className="d-inline-block mr-5">
                <div className="br-radio">
                  <input
                    id="sort-modification-date"
                    name="sort-on"
                    type="radio"
                    checked={sortOn === 'modification_date'}
                    onChange={() => setSortOn('modification_date')}
                  />
                  <label htmlFor="sort-modification-date">
                    {intl.formatMessage(messages.sortByModificationDate)}
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7 mb-3">
              <label className="d-block mb-2 fw-bold">
                {intl.formatMessage(messages.sortOrder)}
              </label>
              <div className="d-inline-block mr-5">
                <div className="br-radio">
                  <input
                    id="sort-asc"
                    name="sort-order"
                    type="radio"
                    checked={sortOrder === 'asc'}
                    onChange={() => setSortOrder('asc')}
                  />
                  <label htmlFor="sort-asc">
                    {intl.formatMessage(messages.ascending)}
                  </label>
                </div>
              </div>
              <div className="d-inline-block mr-5">
                <div className="br-radio">
                  <input
                    id="sort-desc"
                    name="sort-order"
                    type="radio"
                    checked={sortOrder === 'desc'}
                    onChange={() => setSortOrder('desc')}
                  />
                  <label htmlFor="sort-desc">
                    {intl.formatMessage(messages.descending)}
                  </label>
                </div>
              </div>
            </div>
            <div className="col-12">
              <button
                className="br-button primary mr-3"
                type="button"
                onClick={handleFilter}
              >
                {intl.formatMessage(messages.filter)}
              </button>
              <button
                className="br-button secondary mr-3"
                type="button"
                onClick={handleClearFilters}
              >
                {intl.formatMessage(messages.clearFilters)}
              </button>
              {loaded && items.length > 0 && (
                <button
                  className="br-button"
                  type="button"
                  onClick={handleExportCsv}
                  disabled={isExporting}
                  title={intl.formatMessage(messages.exportCsv)}
                >
                  <i
                    className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'} mr-1`}
                    aria-hidden="true"
                  ></i>
                  {isExporting
                    ? intl.formatMessage(messages.exportingRecords, {
                        total,
                      })
                    : intl.formatMessage(messages.exportCsv)}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center my-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">
                {intl.formatMessage(messages.loading)}
              </span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="br-message danger" role="alert">
            <div className="icon">
              <i className="fas fa-times-circle fa-lg" aria-hidden="true"></i>
            </div>
            <div
              className="content"
              aria-label={intl.formatMessage(messages.error)}
            >
              {error.message || JSON.stringify(error)}
            </div>
          </div>
        )}

        {/* No Results */}
        {loaded && !loading && items.length === 0 && (
          <div className="br-message info" role="alert">
            <div className="icon">
              <i className="fas fa-info-circle fa-lg" aria-hidden="true"></i>
            </div>
            <div className="content">
              {intl.formatMessage(messages.noResults)}
            </div>
          </div>
        )}

        {/* Tabela GovBR */}
        {loaded && !loading && items.length > 0 && (
          <div className="br-table">
            <table>
              <caption className="visually-hidden">
                {intl.formatMessage(messages.auditLog)}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{intl.formatMessage(messages.action)}</th>
                  <th scope="col">{intl.formatMessage(messages.title)}</th>
                  <th scope="col">{intl.formatMessage(messages.path)}</th>
                  <th scope="col">
                    {intl.formatMessage(messages.contentType)}
                  </th>
                  <th scope="col">
                    {intl.formatMessage(messages.creationUser)}
                  </th>
                  <th scope="col">
                    {intl.formatMessage(messages.creationDate)}
                  </th>
                  <th scope="col">
                    {intl.formatMessage(messages.modificationUser)}
                  </th>
                  <th scope="col">
                    {intl.formatMessage(messages.modificationDate)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td data-th={intl.formatMessage(messages.action)}>
                      {item.action === 'create' &&
                        intl.formatMessage(messages.create)}
                      {item.action === 'modify' &&
                        intl.formatMessage(messages.modify)}
                      {item.action === 'delete' &&
                        intl.formatMessage(messages.delete)}
                      {item.action === 'change-state' &&
                        intl.formatMessage(messages.changeState)}
                      {item.action === 'move' &&
                        intl.formatMessage(messages.move)}
                      {![
                        'create',
                        'modify',
                        'delete',
                        'change-state',
                        'move',
                      ].includes(item.action) && item.action}
                    </td>
                    <td data-th={intl.formatMessage(messages.title)}>
                      <a
                        href={
                          item.uid ? `../resolveuid/${item.uid}` : item.path
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.title}
                      </a>
                    </td>
                    <td data-th={intl.formatMessage(messages.path)}>
                      {item.path}
                    </td>
                    <td data-th={intl.formatMessage(messages.contentType)}>
                      {portalTypeMap[item.portal_type] || item.portal_type}
                    </td>
                    <td data-th={intl.formatMessage(messages.creationUser)}>
                      {item.creation_user}
                    </td>
                    <td data-th={intl.formatMessage(messages.creationDate)}>
                      {item.creation_date}
                    </td>
                    <td data-th={intl.formatMessage(messages.modificationUser)}>
                      {item.modification_user}
                    </td>
                    <td data-th={intl.formatMessage(messages.modificationDate)}>
                      {item.modification_date}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Table Footer - GovBR Pattern */}
            <div className="table-footer">
              <nav
                className="br-pagination"
                aria-label="paginação"
                data-total={total}
                data-current={activePage}
                data-per-page={pageSize}
              >
                <div className="pagination-per-page">
                  <div className="br-select">
                    <div className="br-input">
                      <label htmlFor="per-page-selection">
                        {intl.formatMessage(messages.display)}
                      </label>
                      <input
                        id="per-page-selection"
                        type="text"
                        placeholder=" "
                        value={pageSize}
                        readOnly
                        onClick={() =>
                          setShowPageSizeDropdown(!showPageSizeDropdown)
                        }
                      />
                      <button
                        className="br-button"
                        type="button"
                        aria-label={intl.formatMessage(messages.showList)}
                        tabIndex="-1"
                        data-trigger="data-trigger"
                        onClick={() =>
                          setShowPageSizeDropdown(!showPageSizeDropdown)
                        }
                      >
                        <i className="fas fa-angle-down" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div
                      className="br-list"
                      style={{
                        display: showPageSizeDropdown ? 'block' : 'none',
                      }}
                    >
                      <div
                        className={`br-item ${pageSize === 5 ? 'selected' : ''}`}
                        role="button"
                        tabIndex="0"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handlePageSizeChange(5);
                            setShowPageSizeDropdown(false);
                          }
                        }}
                        onClick={() => {
                          handlePageSizeChange(5);
                          setShowPageSizeDropdown(false);
                        }}
                      >
                        <div className="br-radio">
                          <input
                            id="per-page-5"
                            type="radio"
                            name="per-page"
                            value="5"
                            checked={pageSize === 5}
                            onChange={() => {
                              handlePageSizeChange(5);
                              setShowPageSizeDropdown(false);
                            }}
                          />
                          <label htmlFor="per-page-5">5</label>
                        </div>
                      </div>
                      <div
                        className={`br-item ${pageSize === 10 ? 'selected' : ''}`}
                        role="button"
                        tabIndex="0"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handlePageSizeChange(10);
                            setShowPageSizeDropdown(false);
                          }
                        }}
                        onClick={() => {
                          handlePageSizeChange(10);
                          setShowPageSizeDropdown(false);
                        }}
                      >
                        <div className="br-radio">
                          <input
                            id="per-page-10"
                            type="radio"
                            name="per-page"
                            value="10"
                            checked={pageSize === 10}
                            onChange={() => {
                              handlePageSizeChange(10);
                              setShowPageSizeDropdown(false);
                            }}
                          />
                          <label htmlFor="per-page-10">10</label>
                        </div>
                      </div>
                      <div
                        className={`br-item ${pageSize === 25 ? 'selected' : ''}`}
                        role="button"
                        tabIndex="0"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handlePageSizeChange(25);
                            setShowPageSizeDropdown(false);
                          }
                        }}
                        onClick={() => {
                          handlePageSizeChange(25);
                          setShowPageSizeDropdown(false);
                        }}
                      >
                        <div className="br-radio">
                          <input
                            id="per-page-25"
                            type="radio"
                            name="per-page"
                            value="25"
                            checked={pageSize === 25}
                            onChange={() => {
                              handlePageSizeChange(25);
                              setShowPageSizeDropdown(false);
                            }}
                          />
                          <label htmlFor="per-page-25">25</label>
                        </div>
                      </div>
                      <div
                        className={`br-item ${pageSize === 50 ? 'selected' : ''}`}
                        role="button"
                        tabIndex="0"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handlePageSizeChange(50);
                            setShowPageSizeDropdown(false);
                          }
                        }}
                        onClick={() => {
                          handlePageSizeChange(50);
                          setShowPageSizeDropdown(false);
                        }}
                      >
                        <div className="br-radio">
                          <input
                            id="per-page-50"
                            type="radio"
                            name="per-page"
                            value="50"
                            checked={pageSize === 50}
                            onChange={() => {
                              handlePageSizeChange(50);
                              setShowPageSizeDropdown(false);
                            }}
                          />
                          <label htmlFor="per-page-50">50</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <span className="br-divider d-none d-sm-block mx-3"></span>
                <div className="pagination-information d-none d-sm-flex">
                  <span className="current">
                    {(activePage - 1) * pageSize + 1}
                  </span>
                  &ndash;
                  <span className="per-page">
                    {Math.min(activePage * pageSize, total)}
                  </span>
                  &nbsp;{intl.formatMessage(messages.of)}&nbsp;
                  <span className="total">{total}</span>&nbsp;
                  {intl.formatMessage(messages.itemsShowing)}
                </div>
                <div className="pagination-go-to-page d-none d-sm-flex ml-auto">
                  <div className="br-select">
                    <div className="br-input">
                      <label htmlFor="go-to-selection">
                        {intl.formatMessage(messages.page)}
                      </label>
                      <input
                        id="go-to-selection"
                        type="text"
                        placeholder=" "
                        value={activePage}
                        readOnly
                        onClick={() =>
                          setShowGoToPageDropdown(!showGoToPageDropdown)
                        }
                      />
                      <button
                        className="br-button"
                        type="button"
                        aria-label={intl.formatMessage(messages.showList)}
                        tabIndex="-1"
                        data-trigger="data-trigger"
                        onClick={() =>
                          setShowGoToPageDropdown(!showGoToPageDropdown)
                        }
                      >
                        <i className="fas fa-angle-down" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div
                      className="br-list"
                      style={{
                        display: showGoToPageDropdown ? 'block' : 'none',
                      }}
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <div
                            key={page}
                            className={`br-item ${activePage === page ? 'selected' : ''}`}
                            role="button"
                            tabIndex="0"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                handlePageChange(null, { activePage: page });
                                setShowGoToPageDropdown(false);
                              }
                            }}
                            onClick={() => {
                              handlePageChange(null, { activePage: page });
                              setShowGoToPageDropdown(false);
                            }}
                          >
                            <div className="br-radio">
                              <input
                                id={`go-to-${page}`}
                                type="radio"
                                name="go-to"
                                value={page}
                                checked={activePage === page}
                                onChange={() => {
                                  handlePageChange(null, { activePage: page });
                                  setShowGoToPageDropdown(false);
                                }}
                              />
                              <label htmlFor={`go-to-${page}`}>{page}</label>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
                <span className="br-divider d-none d-sm-block mx-3"></span>
                <div className="pagination-arrows ml-auto ml-sm-0">
                  <button
                    className="br-button circle"
                    type="button"
                    aria-label={intl.formatMessage(messages.goBack)}
                    disabled={!prevBatch}
                    onClick={() =>
                      prevBatch &&
                      handlePageChange(null, { activePage: activePage - 1 })
                    }
                  >
                    <i className="fas fa-angle-left" aria-hidden="true"></i>
                  </button>
                  <button
                    className="br-button circle"
                    type="button"
                    aria-label={intl.formatMessage(messages.goForward)}
                    disabled={!nextBatch}
                    onClick={() =>
                      nextBatch &&
                      handlePageChange(null, { activePage: activePage + 1 })
                    }
                  >
                    <i className="fas fa-angle-right" aria-hidden="true"></i>
                  </button>
                </div>
              </nav>
            </div>
          </div>
        )}
      </div>

      {isClient &&
        createPortal(
          <Toolbar
            pathname={location.pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </div>
  );
};

export default injectLazyLibs(['reactDates', 'moment'])(AuditLogComponent);
